import { TrainingBalanceInfoDialog } from '@/components/dialogs/TrainingBalanceInfoDialog';
import { PillTabs } from '@/components/PillTabs';
import { ViewHeading } from '@/components/typography/ViewHeading';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import InfoCircleIcon from '@/resources/images/icons-info-circle.svg';
import { KLAYO_COLORS } from '@/themes';
import { ITrainingBalance } from '@/types/views/trainingManagerConfiguration';
import { formatCurrency } from '@/utilities/formatCurrency';
import styled from '@emotion/styled';
import { IconButton, Stack, Typography } from '@mui/material';
import OlcLogo from 'klayowebshared/resources/olc-logo.svg';
import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

interface Props {
  theme?: any;
  amount: number;
  isUnlimitedCredit: boolean;
  balances: ITrainingBalance[];
}

export const ManageTraining = (props: Props) => {
  const { theme, amount, isUnlimitedCredit, balances } = props;
  const location = useLocation();
  const { translate } = useLocale();
  const { user, organization } = useAppData();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const showTabs = ['/settings/learning', '/settings/learning/students'].includes(
    location.pathname
  );

  return (
    <Stack
      gap={5}
      sx={{
        padding: { xs: '40px 24px 0', md: '0' },
        background: KLAYO_COLORS.NeonBlueNB025,
      }}
    >
      <Stack direction={'row'} justifyContent='space-between' alignItems='center' gap={3}>
        <Stack direction={'row'} alignItems='start' gap={3}>
          <ViewHeading
            text={translate('settingsNav.training')}
            sx={{
              marginBottom: '0 !important',
            }}
          />
          {user?.isTrainingManager && (
            <Stack>
              <Typography variant='caption'>{translate('manage.currentBalance')}</Typography>
              <Stack direction={'row'} alignItems={'center'} gap={1.5}>
                <Typography variant='body2' fontWeight={500}>
                  {isUnlimitedCredit ? translate('manage.unlimited') : formatCurrency(amount, 2)}
                </Typography>
                <IconButton
                  onClick={() => setInfoDialogOpen(true)}
                  sx={{
                    p: 0,
                  }}
                >
                  <StyledInfoCircleIcon width={20} height={20} />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Stack>
        <Stack
          sx={{
            width: {
              xs: '82px',
              sm: '98px',
            },
            height: {
              xs: '40px',
              sm: '48px',
            },
          }}
        >
          <OlcLogo id='olc-logo' title='Klayo' style={{ width: '100%', height: '100%' }} />
        </Stack>
      </Stack>

      {user?.isTrainingManager && (
        <>
          <TrainingBalanceInfoDialog
            open={infoDialogOpen}
            onClose={() => setInfoDialogOpen(false)}
            balances={balances}
            isUnlimitedCredit={isUnlimitedCredit}
          />
          {showTabs && (
            <PillTabs
              user={user}
              organization={organization}
              theme={theme}
              path={location.pathname}
              pages={{
                courses: {
                  name: translate('manage.courses'),
                  paths: ['/'],
                  isDefault: () => true,
                  hasAccess: () => true,
                },
                students: {
                  name: translate('manage.students'),
                  paths: ['/students'],
                  isDefault: () => true,
                  hasAccess: () => true,
                },
              }}
              basePath='/settings/learning'
            />
          )}
        </>
      )}
      <Outlet />
    </Stack>
  );
};

const StyledInfoCircleIcon = styled(InfoCircleIcon)(() => ({
  width: 20,
  height: 20,

  '& path[fill="#1E88E5"]': {
    fill: KLAYO_COLORS.NeonBlueNB500,
  },
}));
